import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Eraser,
  CheckCircle,
  Download,
  Lock,
  AlertCircle,
  Loader2,
  Save,
  FileEdit,
  Clock,
  GraduationCap,
  Plus,
  GripVertical,
  ExternalLink
} from 'lucide-react';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { formatDateLocal, addDays } from '../utils/dateUtils';
import { useSchedulerData, WAG_COLORS, getEntityColors, LEAVE_LABELS } from '../hooks/useSchedulerData';
import { useSchedulerActions } from '../hooks/useSchedulerActions';
import { DiversModal } from './scheduler/DiversModal';
import { SubProjectPicker } from './scheduler/SubProjectPicker';

export default function AdminScheduler() {
  const navigate = useNavigate();
  const data = useSchedulerData();
  const actions = useSchedulerActions(data);

  const {
    currentDate, setCurrentDate, loading,
    employees, rowEmployees, setRowEmployees,
    projects, assignments, leaves, trainingDays,
    diversSubProjects, diversGroups,
    startOfWeek, weekDays, currentWeekNumber, isPublished,
  } = data;

  const {
    selectedProjectId, setSelectedProjectId,
    isSubmitting,
    showDiversModal, setShowDiversModal,
    diversForm, setDiversForm,
    isGeocoding, isSavingDivers, isWagGroupSelected,
    showSubProjectPicker, setShowSubProjectPicker,
    selectedSubProjectId,
    draggedIndex, dragOverIndex, isDragging,
    handleSaveDraft, handleValidateWeek, handleExportPDF,
    handleProjectSelect, handleSubProjectSelect, getSelectedDisplayName,
    handleDiversAddressSearch, handleCreateDivers,
    handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleDragEnd,
    RenderCellBlock,
  } = actions;

  return (
    <div style={{
      fontFamily: 'Inter, sans-serif',
      position: 'fixed',
      top: '70px',
      left: '16px',
      right: '16px',
      bottom: '16px',
      zIndex: 40,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundColor:'#f8fafc',
      borderRadius: '12px',
      border: '1px solid #e2e8f0'
    }}>

      {/* --- HEADER FIXE --- */}
      <div style={{ background: 'white', borderBottom: '1px solid #e2e8f0', flexShrink: 0, padding: '10px 20px', zIndex: 50 }}>

        {/* Ligne 1 : Navigation & Actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <button type="button" onClick={() => navigate('/')} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px', cursor: 'pointer', color:'#64748b' }}>
              <ArrowLeft size={20} />
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <h1 style={{ margin: 0, fontSize: '1.2rem', color: '#1e293b' }}>Planification</h1>

              <div style={{ display: 'flex', alignItems: 'center', background: '#f1f5f9', borderRadius: '8px', padding: '4px' }}>
                <button type="button" onClick={() => setCurrentDate(addDays(currentDate, -7))} style={{ border: 'none', background: 'white', cursor: 'pointer', padding:'4px 8px', borderRadius:'6px', boxShadow:'0 1px 2px rgba(0,0,0,0.05)' }}><ChevronLeft size={16} /></button>
                <span style={{ padding: '0 12px', fontSize: '0.9rem', fontWeight: '600', color: '#334155' }}>Semaine {currentWeekNumber} • {startOfWeek.toLocaleDateString()}</span>
                <button type="button" onClick={() => setCurrentDate(addDays(currentDate, 7))} style={{ border: 'none', background: 'white', cursor: 'pointer', padding:'4px 8px', borderRadius:'6px', boxShadow:'0 1px 2px rgba(0,0,0,0.05)' }}><ChevronRight size={16} /></button>
              </div>

              {isPublished ? (
                <span style={{ background:'#dcfce7', color:'#166534', padding:'4px 10px', borderRadius:'20px', fontSize:'0.75rem', fontWeight:'800', display:'flex', alignItems:'center', gap:'6px' }}>
                  <Lock size={14} /> PUBLIÉ
                </span>
              ) : (
                <span style={{ background:'#fff7ed', color:'#c2410c', padding:'4px 10px', borderRadius:'20px', fontSize:'0.75rem', fontWeight:'800', display:'flex', alignItems:'center', gap:'6px', border:'1px solid #ffedd5' }}>
                  <FileEdit size={14} /> BROUILLON
                </span>
              )}
            </div>
          </div>

          <div style={{ display:'flex', gap:'10px' }}>
            <button
              type="button"
              onClick={() => {
                const weekStartStr = formatDateLocal(startOfWeek);
                window.open(`/admin/planning-view?week=${weekStartStr}`, 'planning-view', 'width=1920,height=1080,menubar=no,toolbar=no,location=no,status=no');
              }}
              style={{
                display:'flex', alignItems:'center', gap:'8px', padding:'8px 16px',
                background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
                color: 'white',
                border: 'none',
                borderRadius:'8px', fontWeight:'600', cursor:'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(14, 165, 233, 0.3)'
              }}
              title="Ouvrir la vue 2 semaines dans une nouvelle fenêtre (pour TV/grand écran)"
            >
              <ExternalLink size={18} />
              Vue TV (S{currentWeekNumber} & S{currentWeekNumber + 1})
            </button>
            <button type="button" onClick={handleExportPDF} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'8px 16px', background:'white', color:'#334155', border:'1px solid #cbd5e1', borderRadius:'8px', fontWeight:'600', cursor:'pointer' }}>
              <Download size={18} /> PDF
            </button>

            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={isSubmitting}
              style={{
                display:'flex', alignItems:'center', gap:'8px', padding:'8px 16px',
                background:'white', color:'#2563eb', border:'1px solid #2563eb', borderRadius:'8px',
                fontWeight:'600', cursor: isSubmitting ? 'wait' : 'pointer',
                opacity: isSubmitting ? 0.6 : 1
              }}
            >
              {isSubmitting ? <Loader2 size={18} className="animate-spin"/> : <Save size={18} />}
              {isPublished ? 'Repasser en Brouillon' : 'Sauvegarder'}
            </button>

            <button type="button" onClick={handleValidateWeek} disabled={isPublished} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'8px 16px', background: isPublished ? '#e2e8f0' : '#10b981', color: isPublished ? '#94a3b8' : 'white', border:'none', borderRadius:'8px', fontWeight:'600', cursor: isPublished ? 'not-allowed' : 'pointer' }}>
              {isPublished ? <CheckCircle size={18} /> : <Lock size={18} />}
              {isPublished ? 'Publié' : 'Publier'}
            </button>
          </div>
        </div>

        {/* Ligne 2 : Palette d'outils */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', overflowX: 'auto', whiteSpace: 'nowrap', paddingBottom: '5px' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#64748b', textTransform:'uppercase' }}>OUTILS :</div>

          <button
            type="button"
            onClick={() => setSelectedProjectId(selectedProjectId === 'eraser' ? null : 'eraser')}
            disabled={isPublished}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', borderRadius: '20px',
              border: selectedProjectId === 'eraser' ? '2px solid #ef4444' : '1px solid #cbd5e1',
              background: selectedProjectId === 'eraser' ? '#fef2f2' : 'white',
              cursor: isPublished ? 'not-allowed' : 'pointer', opacity: isPublished ? 0.6 : 1
            }}
          >
            <Eraser size={16} color="#ef4444" />
            <span style={{fontSize:'0.85rem', fontWeight:'600', color:'#ef4444'}}>Gomme</span>
          </button>

          <div style={{ width:'1px', height:'24px', background:'#cbd5e1', margin:'0 10px' }}></div>

          <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#64748b', textTransform:'uppercase' }}>CHANTIERS :</div>

          <button
            type="button"
            onClick={() => setShowDiversModal(true)}
            disabled={isPublished}
            title="Créer un nouveau chantier Divers"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '32px', height: '32px', borderRadius: '50%',
              border: '2px dashed #10b981',
              background: 'white',
              cursor: isPublished ? 'not-allowed' : 'pointer',
              opacity: isPublished ? 0.5 : 1,
              transition: 'all 0.2s ease'
            }}
          >
            <Plus size={18} color="#10b981" />
          </button>

          {projects.map(proj => {
            const isSelected = selectedProjectId === proj.id;
            const isDivers = proj.category === 'divers';
            const subProjectsCount = isDivers ? diversSubProjects.filter(sp => sp.parent_project_id === proj.id).length : 0;

            let displayName = proj.name;
            if (isSelected && actions.selectedSubProjectId) {
              const subProj = diversSubProjects.find(sp => sp.id === actions.selectedSubProjectId);
              if (subProj) {
                displayName = `${subProj.divers_number} - ${subProj.name}`;
              }
            }

            return (
              <button
                key={proj.id}
                type="button"
                onClick={() => handleProjectSelect(proj)}
                disabled={isPublished}
                title={isDivers && subProjectsCount > 0 ? `${proj.name} (${subProjectsCount} sous-chantiers)` : proj.name}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', borderRadius: '20px',
                  border: isSelected ? `2px solid ${proj.color}` : '1px solid #e2e8f0',
                  background: 'white',
                  boxShadow: isSelected ? `0 0 0 2px ${proj.color}30` : 'none',
                  cursor: isPublished ? 'not-allowed' : 'pointer', opacity: isPublished ? 0.6 : 1,
                  maxWidth: '280px',
                  position: 'relative'
                }}
              >
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: proj.color, flexShrink: 0 }}></div>
                <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {displayName}
                </span>
                {isDivers && subProjectsCount > 0 && !isSelected && (
                  <span style={{
                    background: getEntityColors(proj.parent_entity).badge,
                    color: getEntityColors(proj.parent_entity).text,
                    fontSize: '0.65rem',
                    fontWeight: '700',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    marginLeft: '4px'
                  }}>
                    {subProjectsCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Indicateur sous-chantier sélectionné */}
        {selectedSubProjectId && selectedProjectId && selectedProjectId !== 'eraser' && (
          <div style={{
            marginTop: '12px',
            padding: '10px 16px',
            background: (() => {
              const proj = projects.find(p => p.id === selectedProjectId);
              return getEntityColors(proj?.parent_entity).bg;
            })(),
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            border: (() => {
              const proj = projects.find(p => p.id === selectedProjectId);
              return `1px solid ${getEntityColors(proj?.parent_entity).border}`;
            })()
          }}>
            <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600' }}>Chantier actif :</span>
            <span style={{
              fontWeight: '700',
              fontSize: '0.95rem',
              color: (() => {
                const proj = projects.find(p => p.id === selectedProjectId);
                return getEntityColors(proj?.parent_entity).text;
              })()
            }}>
              {getSelectedDisplayName()}
            </span>
            <button
              type="button"
              onClick={() => {
                const proj = projects.find(p => p.id === selectedProjectId);
                if (proj?.category === 'divers') {
                  const entityLabel = proj.parent_entity || (proj.name.toUpperCase().includes('WAG') ? 'WAG' : proj.name);
                  setShowSubProjectPicker({ projectId: proj.id, parentEntity: entityLabel });
                }
              }}
              style={{
                marginLeft: 'auto',
                padding: '4px 12px',
                background: 'white',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                fontSize: '0.8rem',
                cursor: 'pointer',
                color: '#64748b',
                fontWeight: '500'
              }}
            >
              Changer
            </button>
          </div>
        )}
      </div>

      {/* --- ZONE PRINCIPALE (GRILLE) --- */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        <div style={{
          flex: 1,
          overflow: 'auto',
          background: '#f8fafc',
          position: 'relative',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
        {loading ? (
          <LoadingSpinner message="Chargement du planning..." />
        ) : (
          <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', background:'white' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 40 }}>
              <tr>
                <th style={{ padding: '12px', textAlign: 'left', minWidth: '220px', background: 'white', borderBottom: '2px solid #e2e8f0', borderRight:'1px solid #e2e8f0', position: 'sticky', left: 0, zIndex: 50, color:'#64748b', fontSize:'0.75rem', fontWeight:'700', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                  COLLABORATEUR
                </th>
                {weekDays.map(day => (
                  <th key={day.toISOString()} style={{ padding: '8px', textAlign: 'center', minWidth: '160px', background: '#f8fafc', borderBottom: '2px solid #e2e8f0', borderRight: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', fontWeight:'600' }}>{day.toLocaleDateString('fr-FR', { weekday: 'long' })}</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: '800', color: '#1e293b' }}>{day.getDate()}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rowEmployees.map((emp, index) => (
                <tr
                  key={`${index}-${emp.id}`}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  style={{
                    opacity: draggedIndex === index ? 0.5 : 1,
                    background: dragOverIndex === index ? '#eff6ff' : 'transparent',
                    transition: 'background 0.15s, opacity 0.15s'
                  }}
                >
                  <td
                    style={{
                      padding: '0',
                      background: dragOverIndex === index
                        ? '#dbeafe'
                        : emp.company === 'wag'
                          ? WAG_COLORS.bg
                          : 'white',
                      borderBottom: '1px solid #f1f5f9',
                      borderRight:'1px solid #f1f5f9',
                      borderLeft: emp.company === 'wag' ? `3px solid ${WAG_COLORS.border}` : 'none',
                      position: 'sticky',
                      left: 0,
                      zIndex: 30,
                      boxShadow: '2px 0 5px -2px rgba(0,0,0,0.05)',
                      transition: 'background 0.15s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
                      <div
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnd={handleDragEnd}
                        style={{
                          padding: '10px 8px',
                          cursor: isDragging ? 'grabbing' : 'grab',
                          color: draggedIndex === index ? '#2563eb' : emp.company === 'wag' ? WAG_COLORS.text : '#94a3b8',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'color 0.15s',
                          flexShrink: 0
                        }}
                        title="Glisser pour réorganiser"
                      >
                        <GripVertical size={18} />
                      </div>
                      {emp.company === 'wag' && (
                        <span style={{
                          fontSize: '0.6rem',
                          background: WAG_COLORS.border,
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontWeight: '700',
                          marginRight: '6px',
                          flexShrink: 0
                        }}>
                          WAG
                        </span>
                      )}
                      <select
                          value={emp.id}
                          onChange={(e) => {
                              const selected = employees.find(x => x.id === e.target.value);
                              if (selected) {
                                  const newRows = [...rowEmployees];
                                  newRows[index] = selected;
                                  setRowEmployees(newRows);
                              }
                          }}
                          style={{
                              flex: 1, height: '100%', border: 'none', padding: '10px 15px 10px 0',
                              fontWeight: '700', fontSize:'0.9rem',
                              color: emp.company === 'wag' ? WAG_COLORS.text : '#1e293b',
                              background: 'transparent', cursor: 'pointer', outline: 'none'
                          }}
                      >
                          {employees.map(opt => (
                              <option key={opt.id} value={opt.id}>{opt.full_name}</option>
                          ))}
                      </select>
                    </div>
                  </td>
                  {weekDays.map(day => {
                    const dStr = formatDateLocal(day);
                    const isWeekend = day.getDay() === 0 || day.getDay() === 6;

                    const leave = leaves.find(l => l.employee_id === emp.id && l.start_date <= dStr && l.end_date >= dStr);
                    const training = trainingDays.find(t => t.employee_id === emp.id && t.day === dStr);

                    const q1 = assignments.find(a => a.employee_id === emp.id && a.assignment_date === dStr && a.period === 'Q1');
                    const q2 = assignments.find(a => a.employee_id === emp.id && a.assignment_date === dStr && a.period === 'Q2');
                    const q3 = assignments.find(a => a.employee_id === emp.id && a.assignment_date === dStr && a.period === 'Q3');
                    const q4 = assignments.find(a => a.employee_id === emp.id && a.assignment_date === dStr && a.period === 'Q4');

                    const p1 = q1 ? (projects.find(p => p.id === q1.project_id) || null) : null;
                    const p2 = q2 ? (projects.find(p => p.id === q2.project_id) || null) : null;
                    const p3 = q3 ? (projects.find(p => p.id === q3.project_id) || null) : null;
                    const p4 = q4 ? (projects.find(p => p.id === q4.project_id) || null) : null;

                    if (training) {
                      return (
                        <td key={dStr} style={{ background: '#f3e8ff', borderBottom:'1px solid #e2e8f0', borderRight:'1px solid #e2e8f0', textAlign:'center', color:'#7e22ce', verticalAlign:'middle' }}>
                          <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:'5px'}}>
                            <GraduationCap size={22} />
                            <span style={{fontSize:'0.7rem', fontWeight:'700'}}>FORMATION</span>
                            <span style={{fontSize:'0.6rem', color:'#9333ea', maxWidth:'90%', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{training.description}</span>
                          </div>
                        </td>
                      );
                    }

                    if (leave) {
                      return (
                        <td key={dStr} style={{ background: '#f1f5f9', borderBottom:'1px solid #e2e8f0', borderRight:'1px solid #e2e8f0', textAlign:'center', color:'#94a3b8', verticalAlign:'middle' }}>
                          <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:'5px', opacity:0.7}}>
                            <AlertCircle size={20} />
                            <span style={{fontSize:'0.7rem', fontWeight:'700'}}>{(LEAVE_LABELS[leave.type] || leave.type).toUpperCase().substring(0,8)}</span>
                          </div>
                        </td>
                      );
                    }

                    const baseStyle: React.CSSProperties = {
                      padding: 0, height: '140px', borderBottom:'1px solid #e2e8f0', borderRight:'1px solid #e2e8f0',
                      background: isWeekend ? '#f9fafb' : 'white', opacity: isPublished ? 0.8 : 1
                    };

                    const isFullDay = p1 && p2 && p3 && p4
                      && p1.id === p2.id && p2.id === p3.id && p3.id === p4.id
                      && q1?.divers_sub_project_id === q2?.divers_sub_project_id
                      && q2?.divers_sub_project_id === q3?.divers_sub_project_id
                      && q3?.divers_sub_project_id === q4?.divers_sub_project_id;
                    const isAmSame = p1 && p2 && p1.id === p2.id
                      && q1?.divers_sub_project_id === q2?.divers_sub_project_id;
                    const isPmSame = p3 && p4 && p3.id === p4.id
                      && q3?.divers_sub_project_id === q4?.divers_sub_project_id;

                    return (
                      <td key={dStr} style={baseStyle}>
                        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                          {isFullDay ? (
                              <div style={{ flex: 1, display: 'flex' }}>
                                  <RenderCellBlock project={p1} assignment={q1} periods={['Q1', 'Q2', 'Q3', 'Q4']} empId={emp.id} dateStr={dStr} isMerged={true} />
                              </div>
                          ) : (
                              <>
                                <div style={{ flex: 1, display: 'flex', borderBottom: '1px dashed #e2e8f0' }}>
                                    {isAmSame ? (
                                        <RenderCellBlock project={p1} assignment={q1} periods={['Q1', 'Q2']} empId={emp.id} dateStr={dStr} isMerged={true} />
                                    ) : (
                                        <>
                                            <RenderCellBlock project={p1} assignment={q1} periods={['Q1']} empId={emp.id} dateStr={dStr} />
                                            <RenderCellBlock project={p2} assignment={q2} periods={['Q2']} empId={emp.id} dateStr={dStr} />
                                        </>
                                    )}
                                </div>
                                <div style={{ flex: 1, display: 'flex' }}>
                                    {isPmSame ? (
                                        <RenderCellBlock project={p3} assignment={q3} periods={['Q3', 'Q4']} empId={emp.id} dateStr={dStr} isMerged={true} />
                                    ) : (
                                        <>
                                            <RenderCellBlock project={p3} assignment={q3} periods={['Q3']} empId={emp.id} dateStr={dStr} />
                                            <RenderCellBlock project={p4} assignment={q4} periods={['Q4']} empId={emp.id} dateStr={dStr} />
                                        </>
                                    )}
                                </div>
                              </>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </div>
      </div>

      {/* Légende Bas */}
      <div style={{ background:'white', padding:'8px 24px', borderTop:'1px solid #e2e8f0', fontSize:'0.75rem', color:'#64748b', display:'flex', gap:'20px', alignItems:'center', flexShrink:0 }}>
        <span style={{fontWeight:'700', textTransform:'uppercase', display:'flex', alignItems:'center', gap:'6px'}}><Clock size={14} /> Horaires :</span>

        <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
            <span style={{fontSize:'0.75rem', fontWeight:'600', color:'#3b82f6'}}>08h</span>
            <span style={{fontSize:'0.7rem', color:'#94a3b8'}}>(Matin)</span>
        </div>
        <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
            <span style={{fontSize:'0.75rem', fontWeight:'600', color:'#d97706'}}>12h</span>
            <span style={{fontSize:'0.7rem', color:'#94a3b8'}}>(Déj)</span>
        </div>
        <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
            <span style={{fontSize:'0.75rem', fontWeight:'600', color:'#6366f1'}}>13h</span>
            <span style={{fontSize:'0.7rem', color:'#94a3b8'}}>(Aprem)</span>
        </div>
        <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
            <span style={{fontSize:'0.75rem', fontWeight:'600', color:'#8b5cf6'}}>17h</span>
            <span style={{fontSize:'0.7rem', color:'#94a3b8'}}>(Soir)</span>
        </div>

        <div style={{width:'1px', height:'16px', background:'#e2e8f0', margin:'0 10px'}}></div>

        <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
            <div style={{width:10, height:10, background:'#f1f5f9', border:'1px solid #cbd5e1'}}></div>
            <span>Congé / Absent</span>
        </div>
      </div>

      {/* MODALE SÉLECTION SOUS-CHANTIER DIVERS */}
      {showSubProjectPicker && (
        <SubProjectPicker
          projectId={showSubProjectPicker.projectId}
          parentEntity={showSubProjectPicker.parentEntity}
          subProjects={diversSubProjects}
          onSelect={handleSubProjectSelect}
          onClose={() => setShowSubProjectPicker(null)}
          onCreateNew={() => {
            setDiversForm({ ...diversForm, parent_project_id: showSubProjectPicker?.projectId || '' });
            setShowSubProjectPicker(null);
            setShowDiversModal(true);
          }}
        />
      )}

      {/* MODALE CRÉATION DIVERS */}
      {showDiversModal && (
        <DiversModal
          diversForm={diversForm}
          setDiversForm={setDiversForm}
          diversGroups={diversGroups}
          isWagGroupSelected={isWagGroupSelected}
          isGeocoding={isGeocoding}
          isSavingDivers={isSavingDivers}
          onClose={() => setShowDiversModal(false)}
          onAddressSearch={handleDiversAddressSearch}
          onCreate={handleCreateDivers}
        />
      )}

    </div>
  );
}
